import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const BG =
  "https://images.unsplash.com/photo-1633348570573-2c70fe9129de?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function AdminLoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      nav("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="hidden md:block md:w-1/2 relative overflow-hidden">
        <img src={BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <p className="eyebrow mb-4">Studio · Admin</p>
          <h1 className="font-serif text-6xl text-white leading-none">
            Seven<span className="italic text-brand-red">7</span>h
          </h1>
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-6 md:p-16">
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={onSubmit}
          className="w-full max-w-sm"
        >
          <p className="eyebrow mb-4">Restricted — Sign In</p>
          <h2 className="font-serif text-5xl text-white mb-10 leading-tight">
            Admin <span className="italic text-neutral-400">access.</span>
          </h2>
          <div className="space-y-8">
            <div>
              <label className="eyebrow block mb-3">Email</label>
              <input
                data-testid={ADMIN.loginEmail}
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-minimal"
              />
            </div>
            <div>
              <label className="eyebrow block mb-3">Password</label>
              <input
                data-testid={ADMIN.loginPassword}
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-minimal"
              />
            </div>
            {error && (
              <p data-testid={ADMIN.loginError} className="text-red-400 text-sm font-mono">
                {error}
              </p>
            )}
            <div className="pt-4 border-t border-white/10">
              <button
                type="submit"
                data-testid={ADMIN.loginSubmit}
                disabled={loading}
                className="btn-primary w-full justify-center"
              >
                {loading ? "Signing in…" : "Sign In"} <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
